import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Map from '../../components/map/Map';
import { getMissions } from '../../services/missionService';
import { getPositionsForMissionAsExpediteur } from '../../services/positionService';
import { useI18n } from '../../i18n/I18nContext';

const ExpediteurDashboard = () => {
  const { t } = useI18n();
  const mapRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const list = await getMissions();
        setMissions(Array.isArray(list) ? list : []);
        setError(null);
      } catch (e) {
        setError(e.message || t('dashboard.expediteur.loadingError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  // Poll live positions for all EN_COURS missions owned by this expediteur
  useEffect(() => {
    let timer = null;
    let isVisible = true;
    const onVisibilityChange = () => { isVisible = !document.hidden; };
    document.addEventListener('visibilitychange', onVisibilityChange);

    const loadPositions = async () => {
      if (!isVisible) return; // pause when tab is hidden
      try {
        const inProgress = missions.filter(m => m && m.status === 'EN_COURS');
        if (inProgress.length === 0) {
          setPositions([]);
          return;
        }
        const results = await Promise.allSettled(
          inProgress.map(m => getPositionsForMissionAsExpediteur(m.id))
        );
        const merged = results
          .filter(r => r.status === 'fulfilled' && Array.isArray(r.value))
          .flatMap(r => r.value);
        setPositions(merged);
      } catch (_) {
        // ignore
      }
    };

    loadPositions();
    timer = setInterval(loadPositions, 10000);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [missions]);

  const stats = useMemo(() => {
    const byStatus = { EN_ATTENTE: 0, EN_COURS: 0, TERMINEE: 0, ANNULEE: 0 };
    let totalWeight = 0;
    let upcoming = 0;
    const now = new Date();
    missions.forEach(m => {
      if (m && m.status && byStatus[m.status] != null) byStatus[m.status] += 1;
      if (m && m.weight) totalWeight += Number(m.weight) || 0;
      if (m && m.departureDate) {
        const d = new Date(m.departureDate);
        if (d > now) upcoming += 1;
      }
    });
    const total = missions.length;
    return { byStatus, total, totalWeight, upcoming };
  }, [missions]);

  const StatusBarChart = ({ data }) => {
    const labels = ['EN_ATTENTE', 'EN_COURS', 'TERMINEE', 'ANNULEE'];
    const colors = { EN_ATTENTE: '#fbbf24', EN_COURS: '#3b82f6', TERMINEE: '#10b981', ANNULEE: '#ef4444' };
    const max = Math.max(1, ...labels.map(k => data[k] || 0));
    return (
      <div className="row g-3">
        {labels.map(k => (
          <div className="col-6" key={k}>
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="small text-muted">{t(`missions.status.${k.toLowerCase()}`)}</span>
              <span className="small fw-semibold">{data[k] || 0}</span>
            </div>
            <div style={{ background: '#eef2f7', borderRadius: 10, height: 8 }}>
              <div style={{ width: `${((data[k] || 0) / max) * 100}%`, background: colors[k], height: 8, borderRadius: 10 }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const DonutChart = ({ value, total, color = '#10b981', bg = '#e5e7eb' }) => {
    const r = 42; const c = 2 * Math.PI * r; const pct = total > 0 ? Math.min(1, value / total) : 0; const dash = c * pct; const gap = c - dash;
    return (
      <svg width="120" height="120" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} stroke={bg} strokeWidth="10" fill="none" />
        <circle cx="50" cy="50" r={r} stroke={color} strokeWidth="10" fill="none" strokeDasharray={`${dash} ${gap}`} transform="rotate(-90 50 50)" strokeLinecap="round" />
        <text x="50" y="54" textAnchor="middle" fontSize="16" fontWeight="700">{Math.round(pct * 100)}%</text>
      </svg>
    );
  };

  return (
    <DashboardLayout>
      <div className="modern-dashboard" style={{ background: '#f8fafc', minHeight: '100vh', padding: '2rem' }}>
        {/* Header */}
        <div className="dashboard-header mb-4">
          <h1 className="display-6 fw-bold text-dark mb-1">{t('dashboard.expediteur.title')}</h1>
          <p className="text-muted mb-0">{t('dashboard.expediteur.subtitle')}</p>
        </div>

        {/* KPI Cards */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card border-0" style={{ borderRadius: '1.125rem', boxShadow: '0 0.75rem 1.875rem rgba(0,0,0,0.12)', background: 'linear-gradient(135deg,#0ea5e9 0%, #2563eb 100%)' }}>
              <div className="card-body text-white">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="opacity-75">{t('dashboard.expediteur.stats.totalMissions')}</span>
                  <i className="bi bi-list-task" style={{ color: '#fff' }}></i>
                </div>
                <div className="h4 mb-0 fw-bold">{stats.total}</div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card border-0" style={{ borderRadius: '1.125rem', boxShadow: '0 0.75rem 1.875rem rgba(0,0,0,0.12)', background: 'linear-gradient(135deg,#3b82f6 0%, #2563eb 100%)' }}>
              <div className="card-body text-white">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="opacity-75">{t('dashboard.expediteur.stats.upcoming')}</span>
                  <i className="bi bi-calendar-week" style={{ color: '#fff' }}></i>
                </div>
                <div className="h4 mb-0 fw-bold">{stats.upcoming}</div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card border-0" style={{ borderRadius: '1.125rem', boxShadow: '0 0.75rem 1.875rem rgba(0,0,0,0.12)', background: 'linear-gradient(135deg,#60a5fa 0%, #2563eb 100%)' }}>
              <div className="card-body text-white">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="opacity-75">{t('dashboard.expediteur.stats.inProgress')}</span>
                  <i className="bi bi-truck" style={{ color: '#fff' }}></i>
                </div>
                <div className="h4 mb-0 fw-bold">{stats.byStatus.EN_COURS}</div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <div className="card border-0" style={{ borderRadius: '1.125rem', boxShadow: '0 0.75rem 1.875rem rgba(0,0,0,0.12)', background: 'linear-gradient(135deg,#93c5fd 0%, #2563eb 100%)' }}>
              <div className="card-body text-white">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="opacity-75">{t('dashboard.expediteur.stats.totalWeight')}</span>
                  <i className="bi bi-speedometer2" style={{ color: '#fff' }}></i>
                </div>
                <div className="h4 mb-0 fw-bold">{Math.round(stats.totalWeight * 10) / 10} t</div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-7">
            <div className="card border-0" style={{ borderRadius: '1.125rem', boxShadow: '0 0.375rem 1.375rem rgba(0,0,0,0.08)' }}>
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">{t('dashboard.expediteur.charts.statusDistribution')}</h6>
                </div>
                <StatusBarChart data={stats.byStatus} />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-5">
            <div className="card border-0" style={{ borderRadius: '1.125rem', boxShadow: '0 0.375rem 1.375rem rgba(0,0,0,0.08)' }}>
              <div className="card-body d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="mb-1">{t('dashboard.expediteur.charts.completionRate')}</h6>
                  <div className="text-muted small">{t('dashboard.expediteur.charts.terminatedVsTotal')}</div>
                </div>
                <DonutChart value={stats.byStatus.TERMINEE} total={stats.total} />
              </div>
            </div>
          </div>
        </div>

        {/* Map Only for now (no tracking) */}
        <div className="modern-card" style={{
          background: '#fff',
          borderRadius: '1.5rem', /* 24px / 16 = 1.5rem */
          boxShadow: '0 0.25rem 1.5625rem rgba(0,0,0,0.08)', /* 4px 25px -> 0.25rem 1.5625rem */
          border: 'none',
          overflow: 'hidden'
        }}>
          <div className="card-header" style={{
            background: '#fff',
            border: 'none',
            padding: '2rem 2rem 1rem'
          }}>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="fw-bold text-dark mb-0">{t('dashboard.expediteur.maps.liveMap')}</h5>
              <div className="d-flex align-items-center text-muted">
                <i className="bi bi-geo-alt me-2"></i>
                <span className="small">{t('dashboard.expediteur.maps.inProgressMissions')}</span>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div style={{ height: '32.5rem' }}>
              <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
                <Map
                  chauffeurs={positions}
                  mapRef={mapRef}
                  containerRef={containerRef}
                />
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-muted small mt-3">{t('dashboard.expediteur.loading')}</div>
        )}
        {error && (
          <div className="alert alert-danger mt-3">{error}</div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ExpediteurDashboard;